// Classic 棋盘的纯展示层:本地模式与竞技模式都用它,差异只在 Verdict 怎么算出来。
//
// 2026-08-03 改版:格子里**不再显示长数字**(人口/面积/GDP 只留 ↑↓)。
//   原来每格写着「46.8M ↑」,一格要 90px,同样宽度只放得下 4 列;改成箭头后腾出空间加了三列。
//   短数字例外 —— 邻国数(0–16)和气温(-4–29)两位以内,直接写在格子里比藏进悬停有用得多。
//
// 列序:REGION · DISTANCE · BORDERS · TEMP · POP · AREA · GDP
//   前四列是"能直接推理"的线索,后三列是纯大小比较,按信息密度从左到右排。
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../i18n';
import { localName, flagSrc, compactNum, type Country } from '../lib/countries';
import { geoTerm } from '../lib/country';
import { IconArrowUp, IconCheck } from '../components/Icons';
import CountryLink from '../components/CountryLink';

/** 数值列线索。unknown = 任一方无数据,渲染成中性格而不是给个错方向。 */
export interface NumClue {
  hit: boolean;
  higher: boolean; // 答案更大 → ↑
  unknown?: boolean;
}

export interface Verdict {
  isHit: boolean;
  regionHit: boolean;
  pop: NumClue;
  area: NumClue;
  borders: NumClue;
  gdpPc: NumClue;
  temp: NumClue;
  distKm: number;
  bearing: number;
  distColor: string;
  pending?: boolean; // 乐观更新:等待服务器线索
}

export interface Row {
  country: Country;
  verdict: Verdict;
  last: boolean;
}

/** 表头:悬停(桌面)或点击(移动端)弹出该列的含义说明。 */
function ColHead({ label, tip }: { label: string; tip: string }) {
  const [open, setOpen] = useState(false);
  const [mobileTip, setMobileTip] = useState<{ left: number; top: number; width: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMobileTip(null);
      return;
    }

    const media = window.matchMedia('(max-width: 560px)');
    const positionTip = () => {
      if (!media.matches || !ref.current) {
        setMobileTip(null);
        return;
      }
      const rect = ref.current.getBoundingClientRect();
      const width = Math.min(280, window.innerWidth - 24);
      const half = width / 2;
      const left = Math.min(window.innerWidth - 12 - half, Math.max(12 + half, rect.left + rect.width / 2));
      setMobileTip({ left, top: rect.bottom + 8, width });
    };

    positionTip();
    window.addEventListener('resize', positionTip);
    window.addEventListener('scroll', positionTip, true);
    media.addEventListener('change', positionTip);
    return () => {
      window.removeEventListener('resize', positionTip);
      window.removeEventListener('scroll', positionTip, true);
      media.removeEventListener('change', positionTip);
    };
  }, [open]);

  return (
    <>
      <span className={`ch${open ? ' open' : ''}`} ref={ref}>
        <button type="button" className="ch-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {label}
        </button>
        <span className="ch-tip" role="tooltip">
          {tip}
        </span>
      </span>
      {mobileTip &&
        createPortal(
          <span
            className="ch-tip ch-tip-mobile"
            role="tooltip"
            style={{ left: mobileTip.left, top: mobileTip.top, width: mobileTip.width }}
          >
            {tip}
          </span>,
          document.body,
        )}
    </>
  );
}

/** 数值格。value 有值就写出来(短数字),否则只画箭头。 */
function NumCell({ clue, title, value }: { clue: NumClue; title: string; value?: string }) {
  if (clue.unknown) return <div className="cell unk" title={title}>–</div>;
  return (
    <div className={`cell ${clue.hit ? 'hit' : 'num'}`} title={title}>
      {value !== undefined && <span className="val">{value}</span>}
      {clue.hit ? (
        value === undefined && <IconCheck width={15} height={15} />
      ) : (
        <span className="arrow">{clue.higher ? '↑' : '↓'}</span>
      )}
    </div>
  );
}

export function ClassicBoard({ rows }: { rows: Row[] }) {
  const { t, lang } = useI18n();
  const boardRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ hasOverflow: false, atEnd: true });

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;

    const updateScrollState = () => {
      const maxScrollLeft = Math.max(0, board.scrollWidth - board.clientWidth);
      const next = {
        hasOverflow: maxScrollLeft > 1,
        atEnd: maxScrollLeft <= 1 || board.scrollLeft >= maxScrollLeft - 1,
      };
      setScrollState((current) =>
        current.hasOverflow === next.hasOverflow && current.atEnd === next.atEnd ? current : next,
      );
    };

    updateScrollState();
    board.addEventListener('scroll', updateScrollState, { passive: true });
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollState);
    resizeObserver?.observe(board);
    window.addEventListener('resize', updateScrollState);

    return () => {
      board.removeEventListener('scroll', updateScrollState);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [rows.length]);

  if (!rows.length) return null;
  const nf = (n: number) => n.toLocaleString(lang);
  return (
    <div
      className={`board-shell${scrollState.hasOverflow ? ' has-overflow' : ''}${scrollState.atEnd ? ' at-end' : ''}`}
    >
      <div
        className="board classic-board"
        ref={boardRef}
        role={scrollState.hasOverflow ? 'region' : undefined}
        aria-label={scrollState.hasOverflow ? t.boardScrollLabel : undefined}
        tabIndex={scrollState.hasOverflow ? 0 : -1}
      >
        <div className="clue-head">
          <span />
          <span />
          <ColHead label={t.cols.region} tip={t.colTips.region} />
          <ColHead label={t.cols.dist} tip={t.colTips.dist} />
          <ColHead label={t.cols.borders} tip={t.colTips.borders} />
          <ColHead label={t.cols.temp} tip={t.colTips.temp} />
          <ColHead label={t.cols.pop} tip={t.colTips.pop} />
          <ColHead label={t.cols.area} tip={t.colTips.area} />
          <ColHead label={t.cols.gdp} tip={t.colTips.gdp} />
        </div>
        {rows.map(({ country: c, verdict: v, last }) => {
          const nb = (c.borders || []).length;
          return (
            <div className={`clue-row${last ? ' reveal' : ''}${v.pending ? ' pending' : ''}`} key={c.cca3}>
              <span className="flag-cell">
                <img className="flag" src={flagSrc(c)} alt="" loading="lazy" />
              </span>
              <CountryLink country={c} lang={lang} className="cell name country-name-link">
                <span>{localName(c, lang)}</span>
              </CountryLink>
              {v.pending ? (
                <>
                  {Array.from({ length: 7 }, (_, i) => (
                    <div className="cell shimmer" key={i} />
                  ))}
                </>
              ) : (
                <>
                  {/* 大区名要走 geo-terms 译名 —— 曾经这里直接渲染原始英文,
                      结果非英语棋盘格全显示 "Europe" 而不是各自的译名 */}
                  <div className={`cell tag ${v.regionHit ? 'hit' : 'miss'}`} title={geoTerm(c.region, lang) || ''}>
                    <span>{geoTerm(c.region, lang) || '—'}</span>
                  </div>
                  <div
                    className={v.isHit ? 'cell hit' : 'cell dist'}
                    style={v.isHit ? undefined : { backgroundColor: v.distColor }}
                    title={`${t.cols.dist}: ${nf(v.distKm)} km`}
                  >
                    {v.isHit ? (
                      <IconCheck width={16} height={16} />
                    ) : (
                      <span className="dir" style={{ transform: `rotate(${v.bearing}deg)` }}>
                        <IconArrowUp />
                      </span>
                    )}
                  </div>
                  {/* 邻国数与气温是两位以内的短数字,直接写在格子里 */}
                  <NumCell clue={v.borders} title={`${t.cols.borders}: ${nb}`} value={String(nb)} />
                  <NumCell
                    clue={v.temp}
                    title={`${t.cols.temp}: ${c.tempC == null ? '—' : c.tempC + '°C'}`}
                    value={c.tempC == null ? undefined : `${Math.round(c.tempC)}°`}
                  />
                  <NumCell clue={v.pop} title={`${t.cols.pop}: ${compactNum(c.population, lang)}`} />
                  <NumCell clue={v.area} title={`${t.cols.area}: ${compactNum(c.area, lang)} km²`} />
                  <NumCell clue={v.gdpPc} title={`${t.cols.gdp}: ${c.gdpPc ? '$' + nf(c.gdpPc) : '—'}`} />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
